import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { activeLocale } from '@/i18n';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Gateway dates are Jackson UTC strings ("2024-01-02T03:04:05.000+0000") or epoch millis. */
function parse(value: string | number) {
  return typeof value === 'number' ? dayjs(value) : dayjs(value.replace(/([+-]\d\d)(\d\d)$/, '$1:$2'));
}

/** A gateway timestamp (Jackson "…+0000" or ISO) as a Date, e.g. for a DateTimeInput; null when absent or invalid. */
export function parseInstant(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = parse(value);
  return d.isValid() ? d.toDate() : null;
}

/**
 * Date and time for tables and dense blocks: "2024-01-02 03:04", the same in every language so a
 * column scans at a glance. "—" when empty.
 */
export function formatDateTime(value: string | number | null | undefined, tz?: string): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = parse(value);
  if (!d.isValid()) return String(value);
  return (tz ? d.tz(tz) : d).format('YYYY-MM-DD HH:mm');
}

/** Date and time in a sentence, in the active language ("Sep 30, 2026, 10:00 AM", "2026年9月30日 10:00"). */
export function formatLongDateTime(value: string | number | null | undefined, tz?: string): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = parse(value);
  if (!d.isValid()) return String(value);
  return new Intl.DateTimeFormat(activeLocale(), { dateStyle: 'medium', timeStyle: 'short', timeZone: tz }).format(d.toDate());
}

/** "3 minutes ago", "in 2 days", in the active language. */
export function formatRelative(value: string | number | null | undefined, now: number = Date.now()): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = parse(value);
  if (!d.isValid()) return String(value);
  const seconds = Math.round((d.valueOf() - now) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  const rtf = new Intl.RelativeTimeFormat(activeLocale(), { numeric: 'auto' });
  for (const [unit, size] of units) if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  return rtf.format(seconds, 'second');
}

/**
 * Money in the record's currency (USD unless the API says otherwise), formatted for the active
 * language: "$7.17", "7,17 $US". Values arrive as JSON numbers (BigDecimal).
 */
export function formatMoney(value: number | null | undefined, currency = 'USD'): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(activeLocale(), { style: 'currency', currency }).format(value);
}

/** A count or measure with the active language's grouping ("10,035", "10 035"). */
export function formatNumber(value: number | null | undefined, options?: Intl.NumberFormatOptions): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(activeLocale(), options).format(value);
}

/** Keeps the last 4 digits of a phone number visible. */
export function maskPhone(phone: number | string | null | undefined): string {
  const s = String(phone ?? '');
  // Fixed-width mask: the last four digits only, without revealing the number's length.
  return s.length <= 4 ? s : '•••' + s.slice(-4);
}
